import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Auction } from '../../types/auction.types';

/**
 * EDIT SALE — lets an Admin/Auctioneer change the sale's end time and description text
 * from the Auction Management page. The parent (AuctionDetails.handleEditSale) does the
 * actual write, in a transaction, and — CRITICALLY — cascades an end-time change to every
 * lot by shifting each lot's clock by the same delta (matching the terminal move-close).
 *
 * Why the cascade matters: the app derives every countdown / OPEN-CLOSED from each lot's
 * actualEndTime||scheduledEndTime (see utils/saleState.ts), NOT from auction.endDate. So the
 * header end time alone is cosmetic; the lots are the source of truth. This dialog surfaces
 * that explicitly (the "shifts all N lots by X" preview) so the operator knows what happens.
 */

export interface EditSalePayload {
  title: string;          // auction.title — the sale name, public everywhere
  endDate: Date;          // new sale end (header anchor); lots get shifted by the delta
  description: string;    // auction.description
  dateTimeInfo: string;   // auction.saleInfo.dateTimeInfo (the public "closing" blurb)
}

interface EditSaleDialogProps {
  auction: Auction;
  onClose: () => void;
  onSave: (payload: EditSalePayload) => Promise<void>;
}

// <input type="datetime-local"> wants local wall-clock "yyyy-MM-dd'T'HH:mm".
const toLocalInput = (d?: Date | null) => (d && !isNaN(d.getTime()) ? format(d, "yyyy-MM-dd'T'HH:mm") : '');

const humanizeDelta = (ms: number): string => {
  const sign = ms >= 0 ? 'later' : 'earlier';
  const abs = Math.abs(ms);
  const days = Math.floor(abs / 86400000);
  const hours = Math.floor((abs % 86400000) / 3600000);
  const mins = Math.floor((abs % 3600000) / 60000);
  const parts = [];
  if (days) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  if (hours) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (mins) parts.push(`${mins} min`);
  return parts.length ? `${parts.join(', ')} ${sign}` : 'no change';
};

export const EditSaleDialog: React.FC<EditSaleDialogProps> = ({ auction, onClose, onSave }) => {
  const [title, setTitle] = useState<string>(auction.title ?? '');
  const [endLocal, setEndLocal] = useState<string>(toLocalInput(auction.endDate));
  const [description, setDescription] = useState<string>(auction.description ?? '');
  const [dateTimeInfo, setDateTimeInfo] = useState<string>(auction.saleInfo?.dateTimeInfo ?? '');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const newEnd = useMemo(() => (endLocal ? new Date(endLocal) : null), [endLocal]);
  const lotCount = auction.lots?.length ?? 0;
  const deltaMs = useMemo(
    () => (newEnd && auction.endDate ? newEnd.getTime() - auction.endDate.getTime() : 0),
    [newEnd, auction.endDate]
  );
  const endChanged = !!newEnd && deltaMs !== 0;
  const textChanged =
    description !== (auction.description ?? '') || dateTimeInfo !== (auction.saleInfo?.dateTimeInfo ?? '');
  const endInvalid = !newEnd || isNaN(newEnd.getTime());
  const nothingToSave = !endChanged && !textChanged;

  const handleSave = async () => {
    if (endInvalid) { setError('Please enter a valid end date and time.'); return; }
    setProcessing(true);
    setError(null);
    try {
      await onSave({ title: title.trim(), endDate: newEnd as Date, description, dateTimeInfo });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Edit Sale</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl" disabled={processing}>×</button>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">{error}</div>
          )}

          {/* End time */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Sale Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full min-h-[44px] rounded-md border border-gray-300 px-3 py-2"
              placeholder="e.g. August Sports Cards Sale"
            />
            <p className="text-xs text-gray-500 mt-1">
              Shown on the sale page, the lot pages, Past Auctions and in emails.
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Sale End (close) time</label>
            <input
              type="datetime-local"
              value={endLocal}
              onChange={(e) => setEndLocal(e.target.value)}
              className="input w-full"
            />
            <p className="text-xs text-gray-600 mt-1">
              Current: {auction.endDate ? format(auction.endDate, 'EEE MMM d, yyyy h:mm a') : '—'} (your local time)
            </p>
            {endChanged && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-900">
                <strong>Cascades to lots:</strong> every one of the {lotCount} lots will shift{' '}
                <strong>{humanizeDelta(deltaMs)}</strong>, preserving their current spacing. New close:{' '}
                {newEnd ? format(newEnd, 'EEE MMM d, yyyy h:mm a') : '—'}.
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Sale Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="input w-full"
              placeholder="Shown on the Auction Details page and used in Go-Live checks."
            />
          </div>

          {/* Closing / Date-Time info (public blurb on the Sale Info page) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Closing / Date &amp; Time Info</label>
            <textarea
              value={dateTimeInfo}
              onChange={(e) => setDateTimeInfo(e.target.value)}
              rows={3}
              className="input w-full"
              placeholder='Public "starts closing at 8:00 PM Eastern…" blurb on the Sale Info page.'
            />
            <p className="text-xs text-gray-600 mt-1">
              This is the sale-terms blurb bidders read. If a specific date here is stale, fix it here.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <button onClick={onClose} className="btn-secondary" disabled={processing}>Cancel</button>
            <button
              onClick={handleSave}
              disabled={processing || nothingToSave || endInvalid}
              className="btn-primary"
            >
              {processing ? 'Saving…' : endChanged ? `Save & shift ${lotCount} lots` : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
